import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, AlertTriangle, CheckCircle2, Wrench, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_TYPES = ['razorpay', 'razorpay_link', 'cash', 'manual', 'bank_transfer', 'credits', 'internal'];

export const BookingRepairModal = ({ open, onClose, bookingId, api, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    created_by: '',
    payment_type: '',
    razorpay_payment_id: '',
    razorpay_payment_link_id: '',
    total_amount: '',
    reason: '',
  });

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const [diag, u] = await Promise.all([
        api.get(`/admin-bookings/${bookingId}/diagnose`),
        api.get('/users'),
      ]);
      setDiagnosis(diag.data);
      const all = Array.isArray(u.data) ? u.data : (u.data.users || []);
      setUsers(all.filter(x => ['sales', 'center_manager', 'cluster_manager', 'admin', 'hub_manager'].includes(x.role)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to diagnose');
    } finally {
      setLoading(false);
    }
  }, [api, bookingId]);

  useEffect(() => {
    if (open) {
      setForm({ created_by: '', payment_type: '', razorpay_payment_id: '', razorpay_payment_link_id: '', total_amount: '', reason: '' });
      load();
    }
  }, [open, load]);

  const submit = async () => {
    // Only send changed fields
    const body = { reason: form.reason || undefined };
    if (form.created_by) body.created_by = form.created_by;
    if (form.payment_type) body.payment_type = form.payment_type;
    if (form.razorpay_payment_id) body.razorpay_payment_id = form.razorpay_payment_id.trim();
    if (form.razorpay_payment_link_id) body.razorpay_payment_link_id = form.razorpay_payment_link_id.trim();
    if (form.total_amount) body.total_amount = parseFloat(form.total_amount);

    if (Object.keys(body).filter(k => k !== 'reason').length === 0) {
      toast.error('Nothing to change. Fill at least one field.');
      return;
    }

    setSaving(true);
    try {
      const res = await api.patch(`/admin-bookings/${bookingId}/reattribute`, body);
      toast.success('Booking updated. Audit trail recorded.');
      setDiagnosis(prev => prev ? { ...prev, booking: res.data.booking } : null);
      setForm(f => ({ ...f, created_by: '', payment_type: '', razorpay_payment_id: '', razorpay_payment_link_id: '', total_amount: '', reason: '' }));
      onDone?.();
      // Refresh diagnosis
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const b = diagnosis?.booking;
  const zoho = diagnosis?.zoho;
  const issues = diagnosis?.issues || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-repair-modal">
        <DialogHeader>
          <DialogTitle>Diagnose & Repair Booking</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : !b ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No booking loaded.</p>
        ) : (
          <div className="space-y-4 py-2">
            {/* Booking snapshot */}
            <div className="p-4 rounded-lg border border-border bg-card/40 space-y-1 text-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm">#{b.id?.slice(0, 8)} · {b.guest_name || 'Client booking'}</span>
                <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
              </div>
              <p><span className="text-muted-foreground">Guest email: </span>{b.guest_email || '—'}</p>
              <p><span className="text-muted-foreground">Space: </span>{b.venue_name} · {b.bookable_name}</p>
              <p><span className="text-muted-foreground">Date/Time: </span>{b.booking_date} · {b.start_time} – {b.end_time}</p>
              <p><span className="text-muted-foreground">Total (DB): </span><b>₹{b.total_amount?.toLocaleString('en-IN')}</b></p>
              <p><span className="text-muted-foreground">created_by: </span>{b.created_by || <span className="text-red-500 font-medium">— missing —</span>}</p>
              <p><span className="text-muted-foreground">payment_type: </span>{b.payment_type || <span className="text-red-500 font-medium">— missing —</span>}</p>
              <p><span className="text-muted-foreground">razorpay_payment_id: </span>{b.razorpay_payment_id || '—'}</p>
              <p><span className="text-muted-foreground">razorpay_payment_link_id: </span>{b.razorpay_payment_link_id || '—'}</p>
            </div>

            {/* Health check */}
            {issues.length === 0 ? (
              <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> This booking looks healthy — all attribution + payment fields present.
              </div>
            ) : (
              <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 space-y-2">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {issues.length} issue{issues.length !== 1 ? 's' : ''} detected
                </p>
                <ul className="text-xs space-y-1">
                  {issues.map((iss, i) => (
                    <li key={i}>
                      <b>{iss.field}</b> — {iss.problem}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Zoho drift */}
            {zoho && !zoho.error && (
              <div className="p-3 rounded-md border border-border bg-muted/20 text-xs space-y-1">
                <p className="font-semibold">Zoho Invoice</p>
                <p>Number: {zoho.invoice_number} · Status: {zoho.invoice_status}</p>
                <p>Invoice total: <b>₹{zoho.invoice_total?.toLocaleString('en-IN')}</b> · Balance due: ₹{zoho.invoice_balance?.toLocaleString('en-IN')}</p>
                {Math.abs(zoho.drift || 0) > 0.5 && (
                  <p className="text-red-600 font-medium">Drift vs booking: ₹{zoho.drift?.toLocaleString('en-IN')}</p>
                )}
              </div>
            )}
            {zoho?.error && (
              <p className="text-xs text-muted-foreground italic">{zoho.error}</p>
            )}

            {/* Repair form */}
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3">
              <p className="text-sm font-semibold flex items-center gap-2"><Wrench className="w-4 h-4" /> Repair</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Attribute to (created_by)</Label>
                  <Select value={form.created_by} onValueChange={(v) => setForm(f => ({ ...f, created_by: v }))}>
                    <SelectTrigger data-testid="repair-created-by"><SelectValue placeholder="Leave unchanged" /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Payment type</Label>
                  <Select value={form.payment_type} onValueChange={(v) => setForm(f => ({ ...f, payment_type: v }))}>
                    <SelectTrigger data-testid="repair-payment-type"><SelectValue placeholder="Leave unchanged" /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Razorpay payment id (pay_xxx)</Label>
                  <Input value={form.razorpay_payment_id} onChange={(e) => setForm(f => ({ ...f, razorpay_payment_id: e.target.value }))} placeholder="pay_..." data-testid="repair-rzp-payment-id" />
                </div>
                <div>
                  <Label className="text-xs">Razorpay payment link id (plink_xxx)</Label>
                  <Input value={form.razorpay_payment_link_id} onChange={(e) => setForm(f => ({ ...f, razorpay_payment_link_id: e.target.value }))} placeholder="plink_..." data-testid="repair-rzp-plink-id" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Override total (₹) <span className="text-muted-foreground">— use to reconcile with Zoho invoice</span></Label>
                  <Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm(f => ({ ...f, total_amount: e.target.value }))} placeholder={zoho?.invoice_total ? `Zoho says ${zoho.invoice_total}` : 'Leave unchanged'} data-testid="repair-total-amount" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Reason (audit trail)</Label>
                  <Textarea rows={2} value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. 'Reattributing to Komal after ticket #1234'" />
                </div>
              </div>
            </div>

            {/* Audit log */}
            {(b.admin_repair_log || []).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Repair history ({b.admin_repair_log.length})</summary>
                <div className="mt-2 space-y-2">
                  {b.admin_repair_log.slice().reverse().map((entry, i) => (
                    <div key={i} className="p-2 rounded-md bg-muted/30">
                      <p><b>{entry.by_name}</b> · {entry.at?.slice(0, 19).replace('T', ' ')}</p>
                      {entry.reason && <p className="italic text-muted-foreground">&ldquo;{entry.reason}&rdquo;</p>}
                      <ul className="list-disc list-inside">
                        {entry.changes?.map((c, j) => (
                          <li key={j}><b>{c.field}</b>: <span className="text-muted-foreground">{String(c.from ?? '—')}</span> → {String(c.to)}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={saving || !b} data-testid="repair-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
            Apply Repair
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
